import React, { useEffect, useState } from 'react';
import { changePassword } from '../../services/accountApi';
import styles from './ChangePasswordModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

function validate(current: string, next: string, confirm: string): string | null {
  if (!current) return 'Current password is required';
  if (!next) return 'New password is required';
  if (next.length < 12) return 'New password must be at least 12 characters';
  if (next === current) return 'New password must be different from current';
  if (next !== confirm) return 'Passwords do not match';
  return null;
}

export const ChangePasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent(''); setNext(''); setConfirm('');
      setError(null); setBusy(false); setDone(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate(current, next, confirm);
    if (err) { setError(err); return; }
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
      window.setTimeout(onClose, 1200);
    } catch (apiErr) {
      setError(apiErr instanceof Error ? apiErr.message : 'Failed to update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Change Password</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        {done ? (
          <div className={styles.done}>
            <span className={styles.doneIcon}>✓</span>
            Password change request submitted.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {error && <p className={styles.error}>{error}</p>}

            <label className={styles.field}>
              <span>Current Password</span>
              <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            <label className={styles.field}>
              <span>New Password (min 12 chars)</span>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
            </label>
            <label className={styles.field}>
              <span>Confirm New Password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
            </label>

            <div className={styles.actions}>
              <button type="button" className={styles.cancel} onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={styles.submit} disabled={busy}>
                {busy ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
